import {DateTime} from 'luxon';
import React, {FC, useEffect, useMemo} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {CompanyModel} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {AnalyticsDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/analyticsDataEventObject';
import {getHasChatbotHistoryFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import noResultsImageSrc from '../../../../assets/images/noResults.png';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {findThemedImageInfo, ThemedImageNamesEnum} from '../../../../helpers/imageThemeHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsMetricError,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {SettingsChannelsOverviewTabsEnum} from '../../../../models/url/models/channelsUrlModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../models/url/settingsUrlHelpers';
import {downloadFileAsync} from '../../../../services/browser';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getDefaultAnalyticsReportType} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {updateFiltersAction} from '../../../../store/panels/analytics/filters/analyticsPanelFiltersActions';
import {
  getAnalyticsDatetimes,
  makeGetAnalyticsSelectedNamespace,
} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsParameters} from '../../../../store/panels/analytics/parameters/analyticsParameters';
import {
  getAnalyticsReportIsLoading,
  makeGetAnalyticsMetric,
  StateMetric,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {helpCenterUrl, navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {makeAnalyticsReportTypeUrl} from '../../../helpers/analyticsUrlHelpers';
import {useAnalyticsFilter} from '../../../helpers/hooks/useAnalyticsFilter';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {useAnalyticsTrackingFrontendEvent} from '../../../helpers/tracking/useAnalyticsTracking';
import {useAnalyticsChatbotsFilter} from '../../filters/useAnalyticsChatbotsFilter';
import {AnalyticsErrorState} from '../../reportElements/analyticsErrorState';
import {AnalyticsInfoState} from '../../reportElements/analyticsInfoState';
import {isEmptyMetric} from '../../reportElements/charts/emptyState';
import {ReportTable} from '../../reportElements/table/reportTable';
import {reportInnerContent} from '../../reportViewer/reportCommons';
import {ReportGrid} from '../../reportViewer/reportGrid';
import {ChatbotAiAnswersTable} from './chatbotAiAnswersTable';
import {ChatbotReportHeader} from './chatbotReportHeader';
import {makeCsvFileData} from './chatbotReportHelpers';

/*
 * Constants.
 */

const defaultChatbotAnalyticsColumnIds = [
  'chatbot',
  'flows_initiated',
  'flows_completed',
  'chats_deflected',
  'deflection_rate',
  'drop_off_rate',
  'time_to_completion',
];

/*
 * Props.
 */

export interface ChatbotsProps {
  parameters: AnalyticsParameters;
  panelId: AnalyticsPanelId;
}

/*
 * Styles.
 */

const StyledReportViewerWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
`;

// Overflow wrapper and Viewer content are split into 2 to have the scrollbar to the right.
const StyledReportViewerOverflow = styled.div`
  overflow-y: auto;
  padding-bottom: 48px;
`;

const StyledReportViewerContent = styled(ReportGrid)`
  ${reportInnerContent}
  padding: 0 48px;
`;

const StyledEmptyStateWrapperDiv = styled.div`
  padding: 1px 48px 48px 48px;
`;

const StyledEmptyStateBorderDiv = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  border-radius: 20px;
  border: 2px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

export const Chatbots: FC<ChatbotsProps> = ({parameters, panelId}) => {
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const trackAnalyticsFrontendEvent = useAnalyticsTrackingFrontendEvent(getPanelId);

  const namespace = useAppSelector(makeGetAnalyticsSelectedNamespace(getPanelId));
  const defaultReportType = useAppSelector(getDefaultAnalyticsReportType);

  useEffect(() => {
    if (isTeammateNamespace(namespace) || computeIsGlobalNamespace(namespace)) {
      // Redirect to default report if changing to a teammate or global namespace while on the page.
      dispatch(navigateToLinkAction(makeAnalyticsReportTypeUrl(defaultReportType, parameters)));
    }
  }, [defaultReportType, dispatch, namespace, parameters]);

  const [startDate, endDate] = useAppSelector(getAnalyticsDatetimes);
  const [selectedDecisionTreeIds] = useAnalyticsFilter(AnalyticsFilterTypesEnum.CHATBOTS);
  const company = useAppSelector(getCompany);
  const chatbotHistoryFF = useAppSelector(getHasChatbotHistoryFF);

  const selectedDecisionTreeId =
    selectedDecisionTreeIds?.length === 1 ? selectedDecisionTreeIds[0] : undefined;

  useInitialEffect(() => {
    const startTime = startDate?.toMillis();
    const endTime = endDate?.toMillis();
    trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.REPORT, {
      start_time: startTime,
      end_time: endTime,
    });

    // TODO: move this once we add view support
    trackAnalyticsFrontendEvent(DataEventActionsEnum.VIEW, AnalyticsDataEventObject.VIEW, {
      start_time: startTime,
      end_time: endTime,
    });

    // On unmount, clear the chatbot filters.
    return () => {
      dispatch(updateFiltersAction({[AnalyticsFilterTypesEnum.CHATBOTS]: []}));
    };
  });

  const localize = useLocalize();

  const theme = useUserTheme();
  const searchNoResultsAnywhere = findThemedImageInfo(theme, ThemedImageNamesEnum.SEARCH_NO_RESULTS_ANYWHERE);

  const [chatbotTableData, chatbotTableError] = useAppSelector(
    makeGetAnalyticsMetric(AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE),
  );
  const [aiTableData, aiTableError] = useAppSelector(
    makeGetAnalyticsMetric(AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE),
  );

  const copyableErrorString = computeCopyableErrorString(chatbotTableError, aiTableError);

  const isLoading = useAppSelector(getAnalyticsReportIsLoading);
  const isEmpty = computeIsEmpty(isLoading, chatbotTableData, aiTableData);
  const {isDisabled: hasNoChatbots} = useAnalyticsChatbotsFilter();

  const onSetUpChatbotClick = () => {
    navigateToLink(
      buildSettingsPath({
        namespace,
        rootPage: SettingsRootPagesEnum.CHANNELS,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
        tab: SettingsChannelsOverviewTabsEnum.CHAT_BOTS,
      }),
    );
  };

  const onLearnMoreClick = () => openInBrowser(`${helpCenterUrl}/en/articles/2073`);

  // Similar to ReportViewer, with changes made for Chatbots implementation.
  const renderTables = () => (
    <StyledReportViewerOverflow>
      <StyledReportViewerContent>
        {/* Chatbot performance */}
        <ReportTable
          reportSectionId={ReportElementIdsEnum.CHATBOT_BASE_ANALYTICS}
          metric={AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE}
          defaultColumnIds={defaultChatbotAnalyticsColumnIds}
          isLoading={isLoading}
          disableSortBy
          disableSelection
          disableGlobalFilter
          disableTablePagination
          renderDropdown={() =>
            selectedDecisionTreeId &&
            startDate &&
            endDate &&
            chatbotHistoryFF &&
            renderExportCsvButton(company, selectedDecisionTreeId, startDate, endDate)
          }
        />

        {/* AI Answers */}
        <ChatbotAiAnswersTable namespace={namespace} />
      </StyledReportViewerContent>
    </StyledReportViewerOverflow>
  );

  const renderEmptyState = () => (
    <StyledEmptyStateWrapperDiv>
      <StyledEmptyStateBorderDiv>
        <AnalyticsInfoState
          imgPath={noResultsImageSrc}
          imgWidth={searchNoResultsAnywhere.width}
          imgHeight={searchNoResultsAnywhere.height}
          title={localize(
            defineMessage({
              id: 'analytics-metric-chatbot-no-activity-header',
              message: 'No activity',
            }),
          )}
          body={
            <Trans id="analytics-metric-chatbot-no-activity-body">
              No data was found for the selected filters. Try adjusting the filters or select a wider date
              range to get results.
            </Trans>
          }
        />
      </StyledEmptyStateBorderDiv>
    </StyledEmptyStateWrapperDiv>
  );

  const renderNoChatbotsState = () => (
    <StyledEmptyStateWrapperDiv>
      <StyledEmptyStateBorderDiv>
        <AnalyticsInfoState
          imgPath={noResultsImageSrc}
          title={localize(
            defineMessage({
              id: 'analytics-metric-chatbot-no-chatbots-header',
              message: 'No chatbot set up',
            }),
          )}
          body={
            <Trans id="analytics-metric-chatbot-no-chatbots-body">
              Chatbots can gather information about a customer&apos;s inquiry, then directly resolve it or
              pass it along to an agent to handle.
            </Trans>
          }
          actions={
            <>
              <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSetUpChatbotClick}>
                <Trans id="analytics-metric-chatbot-no-chatbots-set-up-a-chatbot">Set up a chatbot</Trans>
              </Button>
              <Button
                variant={ButtonVariantsEnum.LINK}
                rightIcon="externalLinkSmall"
                onClick={onLearnMoreClick}
              >
                <Trans id="analytics-metric-chatbot-no-chatbots-learn-more">Learn more</Trans>
              </Button>
            </>
          }
        />
      </StyledEmptyStateBorderDiv>
    </StyledEmptyStateWrapperDiv>
  );

  function renderPageState() {
    // Error page state
    if (copyableErrorString) {
      return <AnalyticsErrorState copyableErrorString={copyableErrorString} panelId={panelId} />;
    }

    // After loaded & has no chatbots state
    if (!isLoading && hasNoChatbots) {
      return renderNoChatbotsState();
    }

    // No data that matches the filter page state
    if (isEmpty) {
      return renderEmptyState();
    }

    // Loading page state & loaded w/ no errors, present data page state
    return renderTables();
  }

  return (
    <StyledReportViewerWrapper>
      <ChatbotReportHeader panelId={panelId} />

      {renderPageState()}
    </StyledReportViewerWrapper>
  );
};

/*
 * Helpers.
 */

// Checks to see if any numeric cell contains a non-zero value.
function hasNumericCellsWithData(
  tableData: StateMetric<
    AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE | AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE
  >,
) {
  const numericCells = tableData?.rows[0].filter(
    (cell) =>
      cell.type === AnalyticsDataPointTypesEnum.NUMERICAL ||
      cell.type === AnalyticsDataPointTypesEnum.PERCENTAGE ||
      cell.type === AnalyticsDataPointTypesEnum.DURATION,
  );

  return numericCells?.some((cell) => cell.value);
}

const computeIsEmpty = (
  isLoading: boolean,
  chatbotTableData: StateMetric<AnalyticsTableMetricsEnum.CHATBOT_PERFORMANCE_TABLE>,
  aiTableData: StateMetric<AnalyticsTableMetricsEnum.CHATBOT_AI_ANSWERS_TABLE>,
) => {
  const hasEmptyChatTableCells = !hasNumericCellsWithData(chatbotTableData);
  const hasEmptyAiTableCells = !hasNumericCellsWithData(aiTableData);

  return (
    !isLoading &&
    (isEmptyMetric(chatbotTableData) || hasEmptyChatTableCells) &&
    (isEmptyMetric(aiTableData) || hasEmptyAiTableCells)
  );
};

function computeCopyableErrorString(
  chatbotTableError: AnalyticsMetricError | undefined,
  aiTableError: AnalyticsMetricError | undefined,
) {
  if (!chatbotTableError && !aiTableError) {
    return '';
  }

  return JSON.stringify({chatbotTableError, aiTableError});
}

/*
 * Helpers.
 */

function renderExportCsvButton(
  company: CompanyModel,
  decisionTreeId: number,
  startDate: DateTime,
  endDate: DateTime,
) {
  const fileData = makeCsvFileData(company, decisionTreeId, startDate, endDate, 'chat_messages');
  return (
    <Button onClick={() => downloadFileAsync(fileData.url, fileData.name)}>
      <Trans id="chatbot-report-header-filters-csv-chatbot-export-button">Export Chatbot history</Trans>
    </Button>
  );
}
